import { Card, Box, Stack, IconButton, Typography, Tooltip, Badge, useTheme, Button } from "@mui/material";
import { AdjustmentsHorizontalIcon } from "@heroicons/react/24/outline";
import { useLocation, useSearchParams } from "react-router-dom";
import { FC, useState, useMemo, useCallback } from "react";

import AdvancedFiltersPopover from "./organisms/AdvancedFiltersPopover";
import ActiveFiltersChips from "./molecules/ActiveFiltersChip";
import { FilterValues } from "../@types/filter";

export type { FilterValues };
import Collaborator from "../@types/Collaborator";

import React from "react";

interface RefactorFilterProps {
  onFilterUpdate: (filterValues: FilterValues) => void;
  loggedUser?: Collaborator;
  appliedFilterValues: FilterValues;
  draftFilterValues: FilterValues;
  selfRefundsOnly: boolean;
  includeAdvances?: boolean;
  onApplyFilters?: () => void;
  onClearFilters?: () => void;
  onChipRemove?: (filterValues: FilterValues) => void;
}

export const RefundReportFilter: FC<RefactorFilterProps> = React.memo(
  ({ onFilterUpdate, loggedUser, appliedFilterValues, draftFilterValues, selfRefundsOnly, includeAdvances = false, onApplyFilters, onClearFilters, onChipRemove }) => {
    const theme = useTheme();
    const location = useLocation();
    const [, setSearchParams] = useSearchParams();
    const [advancedFiltersAnchor, setAdvancedFiltersAnchor] = useState<HTMLElement | null>(null);

    const [localCollaboratorName, setLocalCollaboratorName] = useState(() => appliedFilterValues.collaboratorName || "");
    const [localCostCenter, setLocalCostCenter] = useState(() => appliedFilterValues.costCenter || "");

    const isRefundRequestPage = useMemo(() => {
      return (
        location.pathname.includes("/form") ||
        location.pathname.includes("/solicitar") ||
        location.pathname.includes("/request")
      );
    }, [location.pathname]);

    const handleClearFilters = useCallback(() => {
      if (onClearFilters) {
        setLocalCollaboratorName("");
        setLocalCostCenter("");
        setAdvancedFiltersAnchor(null);
        onClearFilters();
        return;
      }

      const clearedFilters: FilterValues = {
        collaboratorName: "",
        collaborator: null,
        ownerName: "",
        costCenter: "",
        statusFilter: null,
        startDate: null,
        endDate: null,
        expenseCategory: null,
        ownerApprovedDateUntil: null,
        ownerApprovedDateAfter: null,
        financialApproveDateUntil: null,
        financialApproveDateAfter: null,
        createDateUntil: null,
        createDateAfter: null,
        shouldUpdateSubFilter: true,
        ownerApproved: undefined,
        financialApproved: undefined,
        isAdvance: undefined,
        clientRefund: null,
      };

      if (isRefundRequestPage && loggedUser) {
        clearedFilters.collaborator = loggedUser;
        clearedFilters.collaboratorName = loggedUser.name;
      }

      setLocalCollaboratorName(clearedFilters.collaboratorName || "");
      setLocalCostCenter("");
      setAdvancedFiltersAnchor(null);

      onFilterUpdate(clearedFilters);
      setSearchParams({});
    }, [onFilterUpdate, setSearchParams, isRefundRequestPage, loggedUser, onClearFilters]);

    const getActiveFiltersCount = useMemo(() => {
      let count = 0;
      const isApprovalsOrSearchPage = location.pathname.includes("/approvals") || location.pathname.includes("/search");

      if (
        appliedFilterValues.collaboratorName &&
        appliedFilterValues.collaboratorName.trim() &&
        !isRefundRequestPage &&
        isApprovalsOrSearchPage
      )
        count++;
      if (appliedFilterValues.costCenter && appliedFilterValues.costCenter.trim()) count++;
      if (appliedFilterValues.statusFilter) count++;
      if (appliedFilterValues.startDate) count++;
      if (appliedFilterValues.endDate) count++;

      if (appliedFilterValues.ownerApprovedDateUntil) count++;
      if (appliedFilterValues.ownerApprovedDateAfter) count++;
      if (appliedFilterValues.financialApproveDateUntil) count++;
      if (appliedFilterValues.financialApproveDateAfter) count++;
      if (appliedFilterValues.createDateUntil) count++;
      if (appliedFilterValues.createDateAfter) count++;
      if (appliedFilterValues.expenseCategory) count++;
      if (includeAdvances && appliedFilterValues.isAdvance !== undefined) count++;
      if (appliedFilterValues.clientRefund !== undefined && appliedFilterValues.clientRefund !== null) count++;

      return count;
    }, [appliedFilterValues, includeAdvances, isRefundRequestPage, location.pathname]);

    const hasAnyActiveFilters = useMemo(() => {
      return getActiveFiltersCount > 0;
    }, [getActiveFiltersCount]);

    const handleAdvancedFiltersClick = (event: React.MouseEvent<HTMLElement>) => {
      setAdvancedFiltersAnchor(event.currentTarget);
    };

    const handleAdvancedFiltersClose = () => {
      setAdvancedFiltersAnchor(null);
    };

    return (
      <>
        <Card
          sx={{
            width: "90%",
            margin: 1,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            position: "sticky",
            top: "20px",
            zIndex: 100,
            backgroundColor: "background.paper",
            padding: "1rem",
            overflow: "visible",
          }}
        >
          <Box sx={{ mb: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography sx={{ fontWeight: 600, color: "#00373E" }}>
                  Filtros
                  {getActiveFiltersCount > 0 && (
                    <Typography component="span" variant="body2" sx={{ color: "text.secondary", ml: 1 }}>
                      ({getActiveFiltersCount})
                    </Typography>
                  )}
                </Typography>
              </Box>

              {!hasAnyActiveFilters && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
                    Clique no ícone de filtros para configurar seus filtros de busca
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                {hasAnyActiveFilters && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleClearFilters}
                    sx={{
                      color: "error.main",
                      borderColor: "error.main",
                      "&:hover": {
                        bgcolor: "error.light",
                        color: "error.contrastText",
                        borderColor: "error.main",
                      },
                      textTransform: "none",
                      fontSize: "0.875rem",
                    }}
                  >
                    Limpar filtros
                  </Button>
                )}

                <Tooltip title="Filtros Avançados">
                  <IconButton
                    size="large"
                    onClick={handleAdvancedFiltersClick}
                    sx={{
                      color: "text.secondary",
                      "&:hover": {
                        color: "primary.main",
                      },
                      p: 1.5,
                    }}
                  >
                    <Badge badgeContent={getActiveFiltersCount} color="primary" max={9}>
                      <AdjustmentsHorizontalIcon className="w-7 h-7" />
                    </Badge>
                  </IconButton>
                </Tooltip>
              </Box>
            </Stack>

            <ActiveFiltersChips
              filterValues={appliedFilterValues}
              onFilterUpdate={onFilterUpdate}
              onChipRemove={onChipRemove}
              localCollaboratorName={localCollaboratorName}
              localCostCenter={localCostCenter}
              isRefundRequestPage={isRefundRequestPage}
              loggedUser={loggedUser}
              includeAdvances={includeAdvances}
            />
          </Box>
        </Card>

        <AdvancedFiltersPopover
          anchorEl={advancedFiltersAnchor}
          open={Boolean(advancedFiltersAnchor)}
          onClose={handleAdvancedFiltersClose}
          filterValues={draftFilterValues}
          onFilterUpdate={onFilterUpdate}
          includeAdvances={includeAdvances}
          isRefundRequestPage={isRefundRequestPage}
          selfRefundsOnly={selfRefundsOnly}
          onApplyFilters={onApplyFilters}
        />
      </>
    );
  },
);
